// ============================================================================
// smartContinueIntent.js — SMART Build 2L (pending continuation intent)
//
// Persists ONLY the launchSessionId across a login/reload so a GP who arrives at
// /ehr/smart/continue?launchSessionId=… can resume after authenticating. Mirrors
// the existing viewer-return-context pattern (sessionStorage + try/catch).
//
// HARD INVARIANT: the only value ever written to browser storage is the
// launchSessionId. NEVER a token, code, state, codeVerifier, launch param,
// patient id, fhirUser, fhirUserHash, candidate value, or any PHI.
// ============================================================================

import { isValidLaunchSessionId } from './smartLaunchContinuationGate.js';

const SMART_CONTINUE_KEY = 'rg_smart_continue_intent';

/** Persist the pending launchSessionId. Returns true on success. No-op on invalid id. */
export function setSmartContinueIntent(launchSessionId) {
  if (!isValidLaunchSessionId(launchSessionId)) return false;
  try {
    sessionStorage.setItem(SMART_CONTINUE_KEY, JSON.stringify({ launchSessionId }));
    return true;
  } catch {
    return false; // quota / private browsing
  }
}

/** Read the pending launchSessionId without clearing it (null if none/invalid). */
export function peekSmartContinueIntent() {
  try {
    const raw = sessionStorage.getItem(SMART_CONTINUE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isValidLaunchSessionId(parsed?.launchSessionId) ? parsed.launchSessionId : null;
  } catch {
    return null;
  }
}

/** Read and clear the pending launchSessionId (null if none/invalid). */
export function consumeSmartContinueIntent() {
  try {
    const raw = sessionStorage.getItem(SMART_CONTINUE_KEY);
    sessionStorage.removeItem(SMART_CONTINUE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isValidLaunchSessionId(parsed?.launchSessionId) ? parsed.launchSessionId : null;
  } catch {
    return null;
  }
}

/** Clear any pending continuation intent. */
export function clearSmartContinueIntent() {
  try {
    sessionStorage.removeItem(SMART_CONTINUE_KEY);
  } catch {
    /* ignore */
  }
}

export default {
  setSmartContinueIntent,
  peekSmartContinueIntent,
  consumeSmartContinueIntent,
  clearSmartContinueIntent,
};
